import { ChangeEvent, useState } from 'react'
import DOMPurify from 'dompurify'
import { useRouter } from 'next/router'
import {
  asLink,
  Badge,
  Box,
  Button,
  ButtonGroupActions,
  EmptyState,
  FormControl,
  FormLabel,
  HStack,
  ModalDialog,
  ModalDialogHeader,
  ModalDialogBody,
  ModalDialogFooter,
  NoResults,
  Select,
  SidebarMenuList,
  SidebarMenuListItem,
  Stack,
  Switch,
  Text,
  TextBlockMultiLine,
  useMediaQuery
} from '~/design_system'

import { ChevronRight, StarO } from '~/icons'
import { Msg, useMsg } from '~/common/localizationUtils'
import { trackedButtonClick } from '~/third_party/analytics'
import { LoadingSkeletonState } from '~/components/v2/ResultsLayout/LoadingSkeleton'
import { ResultItems } from '~/components/v2/ResultsLayout/ResultItem'
import { SadStates } from '~/components/SadStates'
import { Search } from '~/components/v2/Search'
import { JobTemplatesQueryParams, useJobTemplateListInfiniteQuery } from '~/api/jobTemplate'
import { JSFSelect } from '~/components/jsonschema/VndlyJsonSchemaFormBase/widgets/select'
import { JobStatusDisplay, RecentJobsParams, useJobListInfiniteQuery, usePublishJobHelpTextQuery } from '~/api/jobs'
import ServerError from '~/job/common/ServerError'
import { useJobStatusBadgeProps } from '~/job/job_list/hooks/useJobStatusBadgeProps'
import { featureFlags, useFeatureFlag } from '~/hooks/useFeatureFlag'
import { ItemsList, ItemsListItem, ItemsListItems } from '~/components/v2/ItemsList'
import { SELECT_JOB_TEMPLATE, SELECT_RECENT_JOB } from '../constants'

type BaseModalProps = {
  isOpen: boolean
  onClose: () => void
}

type JobPublishModalProps = {
  fastPath?: boolean
} & BaseModalProps

type NavTypes = {
  name: string
  actionType: string
}

type TabProps = {
  isFastPath: boolean
}

export const JobPublishModal = ({ isOpen, onClose, fastPath = false }: JobPublishModalProps) => {
  const [isMobileSSR] = useMediaQuery('(max-width: 700px)')
  // due to limitation of chakra's size property use of this was necessary on top of @container query
  const styleProps = {
    size: isMobileSSR ? 'full' : '4xl',
    contentProps: { margin: '0px', top: '0px', maxHeight: '100vh', overflow: 'auto' }
  }
  const router = useRouter()
  const isFastPath = (router && router.query?.fast_path === 'True') || fastPath
  const [selectedMenu, setSelectedMenu] = useState<number>(0)
  const { data: helpTextData = { help_text: '', help_text_enabled: false } } = usePublishJobHelpTextQuery()
  const msg = useMsg()
  const NAV_LIST: NavTypes[] = [
    {
      name: msg('job.publish.publish_from_template.label'),
      actionType: SELECT_JOB_TEMPLATE
    },
    {
      name: msg('job.publish.publish_from_recent_job.label', { term_job: msg('term.job', { count: 99 }) }),
      actionType: SELECT_RECENT_JOB
    }
  ]

  const onCloseModal = () => {
    setSelectedMenu(0)
    onClose()
  }

  return (
    <ModalDialog isOpen={isOpen} onClose={onCloseModal} data-testid="job-publish-modal" {...styleProps}>
      <ModalDialogHeader>
        <Msg id="job.publish.title" term_job={<Msg id="term.job" count={1} />} />
      </ModalDialogHeader>
      <Box
        sx={{
          containerType: 'inline-size',
          containerName: 'jobPublishModalContainer',
          '@container jobPublishModalContainer (min-width: 0px)': {
            '[data-job-publish-desktop-template-tab]': { display: 'none' },
            '[data-job-publish-modal-body]': { overflow: 'auto' },
            '[data-popular-job-desktop-marker]': { display: 'none' },
            '[data-job-publish-mobile-template-tab]': { pb: '12px' },
            '[data-job-publish-modal]': { padding: 12 },
            '[data-job-publish-modal-template-result-container]': { maxHeight: '450px' },
            '[data-job-publish-modal-recent-job-result-container]': { maxHeight: '500px' }
          },
          '@container jobPublishModalContainer (min-width: 500px)': {
            '[data-job-publish-mobile-template-tab]': { display: 'none' },
            '[data-job-publish-desktop-template-tab]': { display: 'block' },
            '[data-popular-job-mobile-marker]': { display: 'none' },
            '[data-popular-job-desktop-marker]': { display: 'block' },
            '[data-job-publish-modal-template-result-container]': { maxHeight: '505px' },
            '[data-job-publish-modal-recent-job-result-container]': { maxHeight: '550px' }
          }
        }}>
        <ModalDialogBody pb={0} data-job-publish-modal-body>
          {helpTextData.help_text_enabled && (
            <Box
              pb={6}
              sx={{ a: { textDecoration: 'underline' } }} // TODO: (TomC) find a reason why some css style like link doesn't inherit text decorator
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(helpTextData.help_text, { USE_PROFILES: { html: true } })
              }}
            />
          )}
          <Stack spacing={0} direction={['column', 'row']}>
            <Box data-job-publish-mobile-template-tab>
              <FormControl id="template_tab">
                <FormLabel>
                  <Msg id="job.publish.publish_from.label" />:
                </FormLabel>
                <Select
                  value={selectedMenu}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedMenu(Number(e.target.value))}>
                  {NAV_LIST.map((item, idx) => (
                    <option key={item.actionType} value={idx}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box
              data-job-publish-desktop-template-tab
              flex={0}
              borderLeft="1px solid var(--vndly-color-container-border)"
              borderTop="1px solid var(--vndly-color-container-border)">
              <SidebarMenuList
                w={40}
                spacing={0}
                onChange={val => {
                  setSelectedMenu(Number(val))
                }}>
                {NAV_LIST.map((item, idx) => {
                  return (
                    <SidebarMenuListItem
                      flex={1}
                      key={item.actionType}
                      title={item.name}
                      value={String(idx)}
                      isActive={idx === selectedMenu}
                    />
                  )
                })}
              </SidebarMenuList>
            </Box>
            <Box
              p={4}
              flex={1}
              borderLeft="1px solid var(--vndly-color-container-border)"
              borderRight="1px solid var(--vndly-color-container-border)"
              borderTop="1px solid var(--vndly-color-container-border)">
              {NAV_LIST[selectedMenu].actionType === SELECT_JOB_TEMPLATE && <JobTemplateTab isFastPath={isFastPath} />}
              {NAV_LIST[selectedMenu].actionType === SELECT_RECENT_JOB && <RecentJobsTab isFastPath={isFastPath} />}
            </Box>
          </Stack>
        </ModalDialogBody>
      </Box>
      <ModalDialogFooter>
        <ButtonGroupActions>
          <Button onClick={trackedButtonClick(onCloseModal, 'cancel_publish_job', {})}>
            <Msg id="common.ui.cancel" />
          </Button>
        </ButtonGroupActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

type LocationOption = {
  label: string
  value: string
  country_code: string
}
const JobTemplateTab = ({ isFastPath }: TabProps) => {
  const { data: only_job_title_select } = useFeatureFlag(featureFlags.ONLY_JOB_TITLE_SELECT)
  const router = useRouter()
  const msg = useMsg()
  const [searchValue, setSearchValue] = useState('')
  const [locationFilter, setLocationFilter] = useState<LocationOption[]>([])
  const [filterByPopularItem, setFilterByPopularItem] = useState(false)
  const openNewJobForm = (customJobTitle: string) => {
    const params = new URLSearchParams({ custom_job_title: customJobTitle })
    if (isFastPath) params.set('fast_path', 'True')
    router.push(`/jobs/add_jobs/?${params.toString()}`)
  }
  const queryParams: JobTemplatesQueryParams = useCreateSearchQuery({
    is_active: true,
    title: searchValue,
    is_popular_job: filterByPopularItem,
    applicable_countries: locationFilter.map((opt: LocationOption) => opt.country_code).join(',')
  })
  const { data, isLoading, isError, isSuccess, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useJobTemplateListInfiniteQuery(queryParams)

  const results = data?.pages?.flatMap(page => page.results) || []
  const resultsLength = data ? results.length : 0

  const newJobHref = (selectedId: number, isFastPath: boolean) => {
    let newJobHref = `/jobs/add_jobs/?id=${selectedId}`
    if (isFastPath) newJobHref += '&fast_path=True'
    return newJobHref
  }

  return (
    <Stack w="full" h="100%" spacing={4}>
      <Search
        value={searchValue}
        onChange={setSearchValue}
        data-testid="job-templates-search-input"
        placeholder={msg('job.publish.search_bar.placeholder', {
          term_job_template: msg('term.job_template', { count: 1 })
        })}
      />
      <Box>
        <label htmlFor="location_restrictions_filter">
          <Text textStyle="bodySemi">
            <Msg id="job_template.form.location_filter.label" />
          </Text>
        </label>
        <JSFSelect
          id="location_restrictions_filter"
          name="location_restrictions_filter"
          value={locationFilter}
          isClearable
          multiple
          placeholder={<Msg id="job_template.form.location_filter.place_holder" />}
          options={{
            optionsFromApi: {
              queries: ['query={{value}}'],
              url: '/api/v2/work_sites/',
              transform: "[results.{'value': code, 'country_code': country_code_alpha_3, 'label': name }]"
            },
            preloadFromApi: {
              url: '/api/v2/work_sites/',
              transform: "[results.{'value': code, 'country_code': country_code_alpha_3, 'label': name }]"
            },
            valueLookupFromApi: {
              url: '/api/v2/work_sites/{{value}}',
              transform: "[results.{'value': code, 'country_code': country_code_alpha_3, 'label': name }]"
            }
          }}
          onOptionChange={(options: LocationOption[]) => {
            setLocationFilter(options)
          }}
        />
      </Box>
      <Stack isInline spacing={2}>
        <Switch
          name="popularJobSwitch"
          id="popular-job-switch"
          isChecked={filterByPopularItem}
          onChange={() => setFilterByPopularItem(!filterByPopularItem)}
        />
        <FormLabel htmlFor="popular-job-switch">
          <Msg id="job.publish.show_only_popular_job.text" term_job={<Msg id="term.job" count={99} />} />
        </FormLabel>
      </Stack>
      <Text textStyle="bodySemi">
        <Msg
          id="job.publish.select_job_template.result.label"
          term_job_template={<Msg id="term.job_template" count={1} />}
        />
      </Text>
      <SadStates
        states={[
          {
            when: isLoading,
            render: () => (
              <ItemsListItems>
                <LoadingSkeletonState numItems={6} />
              </ItemsListItems>
            )
          },
          {
            when: isError,
            render: ServerError
          },
          {
            when: isSuccess && resultsLength === 0 && !only_job_title_select && searchValue.length > 0,
            render: () => (
              <EmptyState textAlign="center" background="gray.100" minH={500} pt={200}>
                <TextBlockMultiLine>
                  <Msg id="common.ui.no_term_found" term={<Msg id="common.ui.result" count={99} />} />
                  <Text fontWeight="semibold" data-testid="create-custom-job-message">
                    <Msg id="job.publish.create_new_custom_job.label" new_job_name={searchValue} />
                  </Text>
                  <Button
                    variant="inlineLink"
                    onClick={() => openNewJobForm(searchValue)}
                    data-testid="create-custom-job-button">
                    <Msg id="job.publish.create_custom_job.label" term_job={<Msg id="term.job" count={1} />} />
                  </Button>
                </TextBlockMultiLine>
              </EmptyState>
            )
          },
          {
            when: isSuccess && !resultsLength && locationFilter.length > 0,
            render: () => (
              <EmptyState minH={500}>
                <NoResults
                  data-testid="job-template-no-result-location"
                  title={
                    <Msg
                      id="job_template.form.location_filter.no_result"
                      countries={locationFilter.map((opt: LocationOption) => opt.label).join(', ')}
                    />
                  }
                />
              </EmptyState>
            )
          },
          {
            when: isSuccess && !resultsLength && !hasNextPage,
            render: () => (
              <EmptyState minH={500}>
                <NoResults data-testid="job-template-no-result" />
              </EmptyState>
            )
          }
        ]}>
        <ResultItems overflowY="auto" data-job-publish-modal-template-result-container>
          <ItemsList w="100%" spacing={0}>
            {results.map(item => (
              <ItemsListItem
                data-testid="template-result-item"
                key={item.id}
                p={4}
                cursor="pointer"
                _hover={{ bg: 'rgba(0, 0, 0, 0.04)' }}
                {...asLink(newJobHref(item.id, isFastPath))}>
                <HStack justifyContent="space-between" w="100%">
                  <TextBlockMultiLine>
                    <Text
                      textDecoration="underline"
                      textStyle="bodySemi"
                      maxW={[200, 380]}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      data-testid="template-title">
                      {item.title}
                    </Text>
                    <Text data-testid="template-category">
                      <Msg id="term.category" count={1} />: {item?.job_category_display ?? <Msg id="common.ui.none" />}
                    </Text>
                  </TextBlockMultiLine>
                  <HStack>
                    {item.is_popular_job && (
                      <>
                        <Box data-popular-job-mobile-marker>
                          <StarO fill="blue.400" />
                        </Box>
                        <Text
                          data-popular-job-desktop-marker
                          textStyle="subtextSmall"
                          data-testid="template-popular-job">
                          <Msg id="job.publish.popular_job.label" term_job={<Msg id="term.job" count={1} />} />
                        </Text>
                      </>
                    )}
                    <ChevronRight />
                  </HStack>
                </HStack>
              </ItemsListItem>
            ))}
            {hasNextPage && (
              <Box
                display="flex"
                px={6}
                justifyContent="center"
                py={4}
                borderTop="1px solid var(--vndly-color-container-border)">
                <Button
                  variant="inlineLink"
                  isLoading={isFetchingNextPage}
                  onClick={() => fetchNextPage && fetchNextPage()}>
                  <Msg id="common.ui.load_more" description="Load More" />
                </Button>
              </Box>
            )}
          </ItemsList>
        </ResultItems>
      </SadStates>
    </Stack>
  )
}

const RecentJobsTab = ({ isFastPath }: TabProps) => {
  const msg = useMsg()
  const [searchValue, setSearchValue] = useState('')
  const queryParams: RecentJobsParams = useCreateSearchQuery({
    search_title: searchValue,
    job_template_is_active: true // Filter out jobs whose job template is not active
  })
  const { data, isLoading, isError, isSuccess, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useJobListInfiniteQuery(queryParams)

  const results = data?.pages?.flatMap(page => page.results) || []
  const resultsLength = data ? results.length : 0

  const newJobHref = (selectedId: number, isFastPath: boolean) => {
    let newJobHref = `/jobs/job-form/?clone_job=${selectedId}`
    if (isFastPath) newJobHref += '&fast_path=True'
    return newJobHref
  }
  return (
    <Stack w="full" h="100%" spacing={4}>
      <Search
        value={searchValue}
        onChange={setSearchValue}
        data-testid="recent-jobs-search-input"
        placeholder={msg('job.publish.search_bar.recent_job.placeholder', { term_job: msg('term.job', { count: 1 }) })}
      />
      <TextBlockMultiLine pb={2}>
        <Text textStyle="bodySemi">
          <Msg id="job.publish.select_recent_job.result.label" term_job={<Msg id="term.job" count={1} />} />
        </Text>
        <Text>
          <Msg id="job.publish.select_recent_job.result.sub_label" term_job={<Msg id="term.job" count={1} />} />
        </Text>
      </TextBlockMultiLine>
      <SadStates
        states={[
          {
            when: isLoading,
            render: () => (
              <ItemsListItems>
                <LoadingSkeletonState numItems={6} />
              </ItemsListItems>
            )
          },
          {
            when: isError,
            render: ServerError
          },
          {
            when: isSuccess && !resultsLength && !hasNextPage,
            render: () => (
              <EmptyState minH={500}>
                <NoResults data-testid="recent-job-no-result" />
              </EmptyState>
            )
          }
        ]}>
        <ResultItems overflowY="auto" data-job-publish-modal-recent-job-result-container>
          <ItemsList w="100%" spacing={0}>
            {results.map(item => (
              <ItemsListItem
                key={item.id}
                p={4}
                cursor="pointer"
                _hover={{ bg: 'rgba(0, 0, 0, 0.04)' }}
                data-testid="recent-job-item"
                {...asLink(newJobHref(item.id, isFastPath))}>
                <RecentJobItem title={item.title} statusDisplay={item.status_display} />
              </ItemsListItem>
            ))}
            {hasNextPage && (
              <Box
                display="flex"
                px={6}
                justifyContent="center"
                py={4}
                borderTop="1px solid var(--vndly-color-container-border)">
                <Button
                  variant="inlineLink"
                  isLoading={isFetchingNextPage}
                  onClick={() => fetchNextPage && fetchNextPage()}>
                  <Msg id="common.ui.load_more" description="Load More" />
                </Button>
              </Box>
            )}
          </ItemsList>
        </ResultItems>
      </SadStates>
    </Stack>
  )
}

const RecentJobItem = ({ title, statusDisplay }: { title: string; statusDisplay: JobStatusDisplay }) => {
  return (
    <HStack justifyContent="space-between" w="100%">
      <Box>
        <Text
          textStyle="bodySemi"
          maxW={[200, 380]}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          data-testid="recent-job-title"
          textDecoration="underline">
          {title}
        </Text>
        <Badge {...useJobStatusBadgeProps(statusDisplay)} data-testid="recent-job-status" />
      </Box>
      <ChevronRight />
    </HStack>
  )
}

const useCreateSearchQuery = (searchQuery: Record<string, any>) => {
  const newSearchQuery = { ...searchQuery }
  Object.keys(newSearchQuery).forEach(key => {
    if (newSearchQuery[key].length === 0 || newSearchQuery[key] === false) {
      delete newSearchQuery[key]
    }
  })
  return newSearchQuery
}
